import { zodResolver } from "@hookform/resolvers/zod";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { createEvaluation } from "../../../api/evaluations";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

//Typage des props parents
type Props = {
  show: boolean;
  metaData: any;
  onClose: () => void;
  onSaved?: () => void;
};

enum NoteType {
  NoteClasse = "Note Classe",
  NoteExamen = "Note Examen",
}

enum Session {
  SessionPrincipale = "Session Principale",
  SessionRattrapage = "Session Rattrapage",
  SessionPayante = "Session Payante",
}
// shéma de notre formulaire
const createSchema = z.object({
  label: z.string().nonempty("Le libellé de la classe est requis"),
  abbreviation: z.string().nonempty("L'abréviation est requis"),
  typeNote: z.nativeEnum(NoteType, {
    errorMap: () => ({ message: "Veuillez choisir un type valide;" }),
  }),
  session: z.nativeEnum(Session, {
    errorMap: () => ({ message: "Veuillez choisir un type valide;" }),
  }),
});

type FormFields = z.infer<typeof createSchema>;

const CreateEvaluationModal = ({ metaData, show, onClose, onSaved }: Props) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'une évaluation
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const validMetaData = metaData?.id_level && metaData?.id_evaluationPeriod;
      // si les meta data ne sont pas valide
      if (!validMetaData) {
        throw new Error(
          "Veuillez vous assurez d'avoir choisie une classe et une période d'évaluation valide!",
        );
      }
      const formData = {
        ...data,
        id_level: metaData?.id_level,
        id_evaluationPeriod: metaData?.id_evaluationPeriod,
      };
      await createEvaluation(formData);
      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Evaluation crée avec succèes");
      onClose();
      // console.log("formData: ", data, "\nmetaData: ", metaData);
    } catch (error: any) {
      // console.log("erreur création d'une évaluation: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Création d'une Evaluation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form
            id="evaluation-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Evaluation 1 ou Evaluation 2"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abréviation
                  </label>
                  <input
                    {...register("abbreviation")}
                    type="text"
                    className={
                      errors.abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: Ev1 ou Ev2"
                  />
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Type note</label>
                  <select
                    className={`form-select ${
                      errors.typeNote ? "is-invalid" : ""
                    }`}
                    {...register("typeNote")}
                  >
                    <option value="">Sélectionner le type</option>
                    {Object.entries(NoteType).map(([key, value]) => (
                      <option key={key} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  {errors.typeNote && (
                    <div className="invalid-feedback">
                      {errors.typeNote.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Type session</label>
                  <select
                    className={`form-select ${
                      errors.session ? "is-invalid" : ""
                    }`}
                    {...register("session")}
                  >
                    <option value="">Sélectionner session</option>
                    {Object.entries(Session).map(([key, value]) => (
                      <option key={key} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  {errors.session && (
                    <div className="invalid-feedback">
                      {errors.session.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>
          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="evaluation-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateEvaluationModal,
  [buildPermission(Resource.EVALUATION, Action.CREATE)],
  "masquer",
);
